import { useAction, useFindMany } from "@gadgetinc/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../../api";
import AutocompleteInput from "../../../../tailwind/autocomplete-input";

const tagToOption = ({ name, id }) => ({
  value: name.toLowerCase(),
  label: name,
  id,
});

const MultiTagAutocomplete = ({ selectedTags, onTagsChange, customerId }) => {
  const [, createTag] = useAction(api.tag.create);
  const [{ data: queryTags }, refetch] = useFindMany(api.tag, {
    first: 250,
    select: {
      id: true,
      name: true,
      createdAt: true,
      updatedAt: true,
    },
  });

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [tagAlreadyExists, setTagAlreadyExists] = useState(false);

  const allTags = useMemo(() => {
    if (!queryTags) {
      return [];
    }

    return queryTags
      .map(tagToOption)
      .sort((tagA, tagB) => (tagA.label || "").localeCompare(tagB.label || ""));
  }, [queryTags]);

  useEffect(() => {
    setOptions(allTags);
  }, [allTags]);

  const debounceTimer = useRef();

  const searchTag = useCallback(
    (eventOrString) => {
      const currentValue =
        typeof eventOrString === "string"
          ? eventOrString
          : eventOrString?.target?.value || "";

      setInputValue(currentValue);

      clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(() => {
        if (currentValue.trim() === "") {
          setOptions(allTags);
          setTagAlreadyExists(false);
          return;
        }

        const lowercase = currentValue.toLowerCase();
        const filteredTags = allTags.filter((tag) =>
          tag.label.toLowerCase().includes(lowercase),
        );

        setOptions(filteredTags);
        setTagAlreadyExists(
          filteredTags.some((tag) => tag.value === lowercase),
        );
      }, 300);
    },
    [allTags],
  );

  const updateTagSelection = useCallback(
    (selection) => {
      const foundTag =
        typeof selection === "string"
          ? allTags.find((tag) => tag.value === selection.toLowerCase())
          : allTags.find(
              (tag) => tag.value === selection?.value?.toLowerCase(),
            );

      if (!foundTag) {
        console.error("Tag introuvable :", selection);
        return;
      }

      // Check if tag is already selected
      const isAlreadySelected = selectedTags.some(
        (tag) => tag.id === foundTag.id,
      );

      if (!isAlreadySelected) {
        const newSelectedTags = [...selectedTags, foundTag];
        onTagsChange(newSelectedTags);
      }

      setInputValue("");
    },
    [allTags, selectedTags, onTagsChange],
  );

  const handleCreateTag = useCallback(async () => {
    try {
      setIsCreatingTag(true);

      const newTagResponse = await createTag({
        tag: {
          customer: { _link: customerId },
          name: inputValue,
        },
      });

      const newTag = tagToOption(newTagResponse.data);

      refetch();

      // Check if tag is already selected
      const isAlreadySelected = selectedTags.some(
        (tag) => tag.id === newTag.id,
      );

      if (!isAlreadySelected) {
        const newSelectedTags = [...selectedTags, newTag];
        onTagsChange(newSelectedTags);
      }

      setInputValue("");
    } catch (error) {
      console.error("Unable to create a tag :", error);
    } finally {
      setIsCreatingTag(false);
    }
  }, [createTag, customerId, inputValue, refetch, selectedTags, onTagsChange]);

  const createActionConfig = useMemo(() => {
    if (inputValue === "" || tagAlreadyExists) {
      return null;
    }

    return {
      accessibilityLabel: "Create new tag",
      content: `Create ${inputValue}`,
      value: inputValue,
      onAction: handleCreateTag,
    };
  }, [inputValue, tagAlreadyExists, handleCreateTag]);

  return (
    <AutocompleteInput
      value={inputValue}
      onChange={searchTag}
      options={options}
      selected={null} // We don't use single selection here
      onSelect={updateTagSelection}
      disabled={isCreatingTag}
      actionBefore={createActionConfig}
    />
  );
};

export default MultiTagAutocomplete;
